import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { meetlyApi, type MeetlyChunkProgress, type MeetlyChunkView } from '../api/meetly';
import type { MeetlyStatusChangedEvent } from './useNotificationSocket';

/**
 * What the recorder shows per 20-second chunk while a meeting runs: its state
 * and the words Meetly heard in it.
 *
 * Two feeds, merged: the realtime `meetly-status-changed` frame carries the
 * chunk that just finished (`progress.last_chunk`), and a fetch of
 * `/meetly/sessions/{id}/chunks` on start and every `pollMs` fills anything a
 * missed frame left behind. Locally-known chunks (uploading) are shown as
 * pending until the server says otherwise.
 */

export interface LiveChunksState {
  chunks: MeetlyChunkView[];
  counts: Partial<MeetlyChunkProgress>;
  /** Most recent chunk with words, for the "heard so far" preview. */
  lastHeard: MeetlyChunkView | null;
}

export const EMPTY_LIVE_CHUNKS: LiveChunksState = { chunks: [], counts: {}, lastHeard: null };

/** Pure merge step: a frame's progress into the known chunk map. Exported for tests. */
export function applyProgress(
  state: LiveChunksState,
  progress: MeetlyChunkProgress | null | undefined,
): LiveChunksState {
  if (!progress) return state;
  const { last_chunk: last, ...counts } = progress;
  let chunks = state.chunks;
  if (last) {
    const index = chunks.findIndex((c) => c.chunk_index === last.chunk_index);
    chunks = index >= 0
      ? chunks.map((c, i) => (i === index ? last : c))
      : [...chunks, last].sort((a, b) => a.chunk_index - b.chunk_index);
  }
  return {
    chunks,
    counts: { ...state.counts, ...counts },
    lastHeard: last && last.state === 'transcribed' && last.text ? last : state.lastHeard,
  };
}

/** A full fetch replaces what we know; the preview keeps the latest words. */
export function applySnapshot(state: LiveChunksState, chunks: MeetlyChunkView[], counts: MeetlyChunkProgress): LiveChunksState {
  const heard = [...chunks].reverse().find((c) => c.state === 'transcribed' && c.text);
  return { chunks, counts, lastHeard: heard ?? state.lastHeard };
}

export function useMeetlyLiveChunks(
  sessionId: string | null,
  meetingId: string | null,
  options: { enabled?: boolean; pollMs?: number } = {},
): LiveChunksState {
  const { enabled = true, pollMs = 15_000 } = options;
  const [state, setState] = useState<LiveChunksState>(EMPTY_LIVE_CHUNKS);
  const activeRef = useRef(false);

  const fetchSnapshot = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await meetlyApi.getSessionChunks(sessionId);
      if (!activeRef.current) return;
      setState((prev) => applySnapshot(prev, data.chunks, data.progress));
    } catch {
      // The realtime frames still arrive; a missed poll is not worth a message.
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !meetingId || !enabled) {
      setState(EMPTY_LIVE_CHUNKS);
      return undefined;
    }
    activeRef.current = true;
    void fetchSnapshot();
    const timer = window.setInterval(() => void fetchSnapshot(), pollMs);

    const onFrame = (event: Event) => {
      const detail = (event as CustomEvent<MeetlyStatusChangedEvent>).detail;
      if (!detail || detail.meetingId !== meetingId) return;
      setState((prev) => applyProgress(prev, detail.progress as MeetlyChunkProgress | null));
    };
    window.addEventListener('meetly-status-changed', onFrame);

    return () => {
      activeRef.current = false;
      window.clearInterval(timer);
      window.removeEventListener('meetly-status-changed', onFrame);
    };
  }, [sessionId, meetingId, enabled, pollMs, fetchSnapshot]);

  return useMemo(() => state, [state]);
}

export default useMeetlyLiveChunks;
